import { Badge, Button, Card, Col, Form, ListGroupItem, OverlayTrigger, Placeholder, Row, Tooltip } from "react-bootstrap";
import { FhirPatientVisit } from "../../amplify/api-gateway/fetch-sync-fhir";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/api-gateway/search-patients';

import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons";
import { faBed, faChevronDown, faChevronUp, faClockRotateLeft, faCreditCard, faEnvelope, faExclamationTriangle, faFire, faFlag, faLevelUp, faMobile, faPerson, faPhone, faSpinner, faStar, faTruckMedical, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { callQuery, Favorite, LOCATIONS_BY_CSN, Note, NOTES_FOR_USERNAME_AND_MRN } from "../../amplify/api-gateway/query";
import { callWrite, DELETE_FAVORITE, INSERT_FAVORITE, INSERT_NOTE } from "../../amplify/api-gateway/write";
import { DISPO_RISK_THRESHOLD } from "../../utils/plot";
import { getHourMinuteDifference, getLatestTime, getLocalDateStrFromDate, getLocalTimeStrFromDate, getUserFriendlyDate } from "../../utils/time";
import { ExpandingTextarea } from "../input/ExpandingTextArea";
import PersonalIdentifier from "../common/PersonalIdentifier";
import { getUserRole, UserRole } from "../../utils/user";
import { useGlobalOptions } from "../common/GlobalOptionsContext";


export interface TrackboardItemProps {
    patient: PatientVisit & FhirPatientVisit & PatientVisitMetadata,
    pastFavorites?: Favorite[],
    pastReviewed?: Favorite[],
    updatePatientWithNote: any,
    mrnToRelevantPastDiagnosesForCompass?: any, // Patients who have specific diagnoses we're looking for for COMPASS study
    csnToFavorites: any,
    csnToReviewed: any,
    setCsnToReviewed: any,
    columns: any[],
    setShowContextMenu: any,
    setContextMenu: any,
    tenant: string,
    padNotes?: boolean,
    isPlaceholder?: boolean,
    user?: any,
}

export const TrackboardItem = (props: TrackboardItemProps) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
    const [isLoadingReviewed, setIsLoadingReviewed] = useState(false);
    const [noteText, setNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [saveTimeout, setSaveTimeout] = useState<any>(null);
    const [lastSavedNoteText, setLastSavedNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [isNoteTextChanged, setIsNoteTextChanged] = useState(false);
    const [events, setEvents] = useState<any[]>([]);
    const [pastNotes, setPastNotes] = useState<Note[]>([]);

    const totalRelativeCols = props.columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);

    // Effect to handle autosave on content change
    useEffect(() => {
        // Clear any existing timeout if content changes
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Set a new timeout to save the content after 2 seconds of inactivity
        const newTimeout = setTimeout(() => {
            if (isNoteTextChanged) {
                saveNote();
            }
        }, 2000);

        // Store the timeout ID so it can be cleared later
        setSaveTimeout(newTimeout);

        // Clean up the timeout when the component unmounts or content changes
        return () => {
            clearTimeout(newTimeout);
        };
    }, [noteText]); // Only trigger effect when content changes

    useEffect(() => {
        if (isExpanded) {
            setIsLoadingLocations(true);
            callQuery({
                operation: LOCATIONS_BY_CSN,
                csn: props.patient.csn,
            })
            .then((locations: any) => {
                updateEvents(locations);
                setIsLoadingLocations(false);
            });

            callQuery({
                operation: NOTES_FOR_USERNAME_AND_MRN,
                mrn: props.patient.mrn,
            })
            .then((notes: Note[]) => {
                const filteredNotes = notes.filter(note => note.csn !== props.patient.csn);
                setPastNotes(filteredNotes);
            });
        }
    }, [isExpanded]);

    const isFavorite = () => {
        if (props.csnToFavorites[props.patient.csn] && props.csnToFavorites[props.patient.csn].length > 0) {
            return true;
        }
        return false;
    }

    const isReviewed = () => {
        if (props.csnToReviewed[props.patient.csn]) {
            return true;
        }
        return false;
    }

    const updateCsnToReviewed = (reviewed?: Favorite) => {
        const map: any = {...props.csnToReviewed};
        if (reviewed) {
            // newly saved favorite
            map[props.patient.csn] = reviewed;
        } else {
            // favorite deleted
            if (map[props.patient.csn]) {
                delete map[props.patient.csn]
            }
        }
        props.setCsnToReviewed(map);
    }

    const handleNoteChange = (text: any) => {
        setIsNoteTextChanged(true);
        setNoteText(text);
    };

    const saveReviewed = () => {
        setIsLoadingReviewed(true);
        callWrite({
            operation: INSERT_FAVORITE,
            favorite_time: new Date().getTime(),
            favorite_type: "reviewed",
            csn: props.patient.csn,
            mrn: props.patient.mrn,
            internal_csn: props.patient.internal_csn,
            internal_mrn: props.patient.internal_mrn,
        })
        .then((output: Favorite) => {
            console.log("Saved reviewed", output);
            setIsLoadingReviewed(false);
            updateCsnToReviewed(output);
        });
    }

    const deleteReviewed = () => {
        setIsLoadingReviewed(true);
        callWrite({
            operation: DELETE_FAVORITE,
            favorite_type: "reviewed",
            csn: props.patient.csn,
        })
        .then((output: Favorite) => {
            console.log("Deleted reviewed", output);
            // Purposefully don't pass output to update code to indicate deletion
            setIsLoadingReviewed(false);
            updateCsnToReviewed();
        });
    }

    const saveNote = () => {
        if (lastSavedNoteText !== noteText) {
            setIsSavingNote(true);
            callWrite({
                operation: INSERT_NOTE,
                creation_time: new Date().getTime(),
                note_type: "note",
                csn: props.patient.csn,
                mrn: props.patient.mrn,
                note: noteText,
            })
            .then((output: Note[]) => {
                console.log("Saved note", output);
                setIsSavingNote(false);
                setNoteText(output[0].note);
                props.updatePatientWithNote(output[0]);
                setLastSavedNoteText(output[0].note);
            });
        }
    }

    const getWidth = (col: any) => {
        return `${100 * col.size / totalRelativeCols}%`;
    }

    const getAcuity = () => {
        let badgeVariant = "light";
        let className = "";
        if (props.patient.triage_acuity) {
            const val = props.patient.triage_acuity;
            if (val.startsWith("1")) {
                badgeVariant = "danger";
            } else if (val.startsWith("2")) {
                badgeVariant = "warning";
                className = "trackboard-bg-emergent";
            } else if (val.startsWith("3")) {
                badgeVariant = "warning";
                className = "trackboard-bg-urgent";
            } else {
                className = "trackboard-bg-other";
            }
        }
        return <Badge bg={badgeVariant} className={className}>{props.patient.triage_acuity}</Badge>
    }

    const getElapsedTime = () => {
        const dt = new Date(props.patient.arrival_time as string);
        let currTime = getLatestTime(props.patient, props.tenant);
        return `${getHourMinuteDifference(dt, new Date(currTime))}`
    }

    const calculateDuration = (start?: string, end?: string) => {
        if (!start || !end) {
            return <></>
        }

        const startDate = new Date(start);
        const endDate = new Date(end);
        return <div className="timeline-duration">Duration: {getHourMinuteDifference(startDate, endDate)}</div>
    };

    const timeZoneAbbreviation = new Date().toLocaleTimeString('en', { timeZoneName: 'short' }).split(' ')[2];

    const updateEvents = (locations: any[]) => {
        const events: any = [];
        events.push({
            "name": "Arrived",
            "time": new Date(props.patient.arrival_time),
            "duration": null,
        });
        if (props.patient.roomed_time) {
            events.push({
                "name": "Roomed",
                "time": new Date(props.patient.roomed_time),
                "duration": null,
            });
        }
        if (props.patient.dispo_time) {
            events.push({
                "name": "Dispo",
                "time": new Date(props.patient.dispo_time),
                "duration": null,
            });
        }
        if (props.patient.departure_time) {
            events.push({
                "name": "Departure",
                "time": new Date(props.patient.departure_time),
                "duration": null,
            });
        }
        if (locations) {
            for (let location of locations) {
                events.push({
                    "name": `Room: ${location.location}`,
                    "time": new Date(location.location_start),
                    "duration": null,
                });
            }
        }
        events.sort((a: any, b: any) => a.time - b.time);

        // Compute the duration between every event
        for (let i = 0; i < (events.length - 1); i++) {
            events[i].duration = calculateDuration(events[i].time, events[i + 1].time);
        }
        setEvents(events);
    }

    const formatSecondsToHHMM = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
    
        const paddedHours = `${hours}`.padStart(2, '0');
        const paddedMinutes = `${minutes}`.padStart(2, '0');
    
        return `${paddedHours}:${paddedMinutes}`;
    };
    

    return (
        <ListGroupItem className="d-flex flex-column trackboard-item">
            <div style={{ display: 'block', width: '100%' }} onClick={() => window.open(`/dashboard/${props.patient.internal_csn}`, "_blank")}>
                {
                    props.columns.map((col: any, idx: number) => {
                        if (props.isPlaceholder) {
                            return (
                                <div key={`placeholder-${idx}`} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={6} size="xs" />
                                    </Placeholder>
                                </div>
                            )
                        }
                        if (col.id === "reviewed") {
                            return (
                                <div key={`reviewed-${col.id}`} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {
                                        isLoadingReviewed ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                        ) : (
                                            <Form.Check
                                                type="checkbox"
                                                checked={isReviewed()}
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                }}
                                                onChange={(e: any) => {
                                                    e.stopPropagation();
                                                    setIsLoadingReviewed(true);
                                                    if (isReviewed()) {
                                                        deleteReviewed();
                                                    } else {
                                                        saveReviewed();
                                                    }
                                                }}
                                            />
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "arrival_time") {
                            if (props.patient.arrival_time) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <span>{getLocalDateStrFromDate(new Date(props.patient.arrival_time))} {getLocalTimeStrFromDate(new Date(props.patient.arrival_time))}</span>
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "name") {
                            if (props.patient.given || props.patient.given === "") {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <span><PersonalIdentifier identifier={`${props.patient.family}, ${props.patient.given}`} /></span>
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "languages") {
                            if (props.patient.given || props.patient.given === "") {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {props.patient.languages && props.patient.languages.length > 0 && props.patient.languages.join(", ")}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "total_mon_time") {
                            if (props.patient.total_mon) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {formatSecondsToHHMM(props.patient.total_mon)}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "afib_burden") {
                            if (props.patient.afib_total_sec && props.patient.total_mon) {
                                const percentage = (props.patient.afib_total_sec / props.patient.total_mon) * 100;
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {`${percentage.toFixed(2)}%`}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "longest_afib_episode") {
                            if (props.patient.afib_epi) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {formatSecondsToHHMM(props.patient.afib_epi)}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                       <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "triage_acuity") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {getAcuity()}
                                </div>
                            )
                        } else if (col.id === "elapsed") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {getElapsedTime()}
                                </div>
                            )
                        } else if (col.id === "admit_score") {
                            const isHigh = props.patient.admit_score && Number(props.patient.admit_score) >= DISPO_RISK_THRESHOLD;
                            // Note that at the request of the COMPASS research coordinators, not making it red to avoid confusion with revisit risk
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "" : "" }}>
                                    {props.patient.admit_score !== undefined ?  Number(props.patient.admit_score).toFixed(2) : ""}{' '}
                                    {isHigh && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Patient likely to be admitted (&gt; 0.534)
                                                </Tooltip>
                                            }
                                        >
                                            <span><FontAwesomeIcon icon={faExclamationTriangle} /></span>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_present") {
                            const isHigh = props.patient.afib_present && props.patient.afib_present.length > 0 &&  Number(props.patient.afib_present[0]) >= 0.5;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.afib_present && props.patient.afib_present.length > 0 ? "Y" : "-"}{' '}
                                    {isHigh && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Afib detected in at least one ECG segment
                                                </Tooltip>
                                            }
                                        >
                                            <span><FontAwesomeIcon icon={faExclamationTriangle} /></span>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_no_prev") {
                            // # 0 = No previous data
                            // # 1 = Previous data, no arrhythmia and no anti-coag
                            // # 3 = Previous data, arrhythmia, no anti-coag
                            // # 5 = Previous data, no arrhythmia, anti-coag
                            // # 7 = Previous data, arrhytnmia, anti-coag
                            if (!props.patient.afib_no_prev || props.patient.afib_no_prev.length === 0) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        -
                                    </div>
                                )
                            }

                            const prevCriteriaScore = Number(props.patient.afib_no_prev[0]);
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: prevCriteriaScore === 1 ? "red" : "" }}>
                                    {prevCriteriaScore === 0 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    No past historical data available for patient
                                                </Tooltip>
                                            }
                                        >
                                            <span>No Data</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 1 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    No past arrhythmia and no prior anti-coag med
                                                </Tooltip>
                                            }
                                        >
                                            <span>No History</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 3 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past arrhythmia but no anti-coag
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior Arrhythmia</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 5 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past anti-coagulant but no prior arrhythmia
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior Anti-Coag</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 7 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past anti-coagulant and prior arrhythmia
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior History</span>
                                        </OverlayTrigger>
                                    ) : (
                                        <>Unknown</>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_cha") {
                            let score = -1;
                            if (props.patient.afib_cha && props.patient.afib_cha.length > 0) {
                                score = Number(props.patient.afib_cha[0]);
                            }
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: score >= 2 ? "red" : "" }}>
                                    {score >= 2 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    CHA2DS2–VASc &gt;= 2 (anti-coagulant recommended)
                                                </Tooltip>
                                            }
                                        >
                                            <span>{score}</span>
                                        </OverlayTrigger>
                                    ) : (
                                        <span>{score >= 0 ? score : "-"}</span>
                                    )}
                                </div>
                            )
                        } else if (col.id === "epic_revisit_score") {
                            const isHigh = props.patient.epic_revisit_score && props.patient.epic_revisit_score.length > 0 &&  Number(props.patient.epic_revisit_score[0]) >= 0.5;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.epic_revisit_score && props.patient.epic_revisit_score.length > 0 ?  Number(props.patient.epic_revisit_score[0]).toFixed(2) : ""}{' '}
                                    {isHigh && <FontAwesomeIcon icon={faExclamationTriangle} />}
                                    {
                                        props.mrnToRelevantPastDiagnosesForCompass && props.mrnToRelevantPastDiagnosesForCompass[props.patient.mrn] && (
                                            <OverlayTrigger
                                                placement="bottom"
                                                overlay={
                                                    <Tooltip>
                                                        Relevant PMH: {props.mrnToRelevantPastDiagnosesForCompass[props.patient.mrn]}
                                                    </Tooltip>
                                                }
                                            >
                                                <span style={{color: '#f5971d'}}><FontAwesomeIcon icon={faFire} /></span>
                                            </OverlayTrigger>
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "triage_hr") {
                            // @ts-ignore
                            const isHigh = props.patient.triage_hr && props.patient.triage_hr !== "NaN" && props.patient.triage_hr >= 110;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.triage_hr}
                                </div>
                            )
                        } else if (col.id === "triage_spo2") {
                            // @ts-ignore
                            const isHigh = props.patient.triage_spo2 && props.patient.triage_spo2 !== "NaN" && props.patient.triage_spo2 < 90;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.triage_spo2}
                                </div>
                            )
                        } else if (col.id === "triage_nbp") {
                            // @ts-ignore
                            const sbp = (!props.patient.triage_sbp || props.patient.triage_sbp === "NaN") ? "-" : Math.round(props.patient.triage_sbp);
                            // @ts-ignore
                            const dbp = (!props.patient.triage_dbp || props.patient.triage_dbp === "NaN") ? "-" : Math.round(props.patient.triage_dbp);

                            let isLow = false;
                            // @ts-ignore
                            if (props.patient.triage_sbp && props.patient.triage_sbp !== "NaN" && props.patient.triage_dbp && props.patient.triage_dbp !== "NaN") {
                                isLow =((2 * props.patient.triage_dbp + props.patient.triage_sbp) / 3) < 65;
                            }
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isLow ? "red" : "" }}>
                                    {sbp}/{dbp}
                                </div>
                            )
                        } else if (col.id === "action") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    <Button
                                        variant="primary"
                                        className={"trackboard-save-btn"}
                                        onClick={(e: any) => {
                                            console.log(e)
                                            e.stopPropagation();
                                            props.setShowContextMenu(true);
                                            props.setContextMenu({
                                                x: e.pageX,
                                                y: e.pageY,
                                                patient: props.patient,
                                                listName: null,
                                            })
                                        }}
                                    >
                                        {
                                            isLoadingFavorite ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : isFavorite() ? (
                                                <>
                                                    <FontAwesomeIcon icon={faStar} />
                                                </>
                                            ) : props.pastFavorites && props.pastFavorites.length > 0 ? (
                                                <>
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip>
                                                                Favorite in previous encounter
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span style={{color: 'red'}}><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                                                    </OverlayTrigger>
                                                </>
                                            ) : (
                                                <>
                                                    {/** @ts-ignore */}
                                                    <FontAwesomeIcon icon={faStarOutline} />
                                                </>
                                            )
                                        }
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="trackboard-expand-btn"
                                        onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                                    >
                                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                    </Button>
                                </div>
                            )
                        } else {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {/* @ts-ignore */}
                                    {props.patient[col.id] ? props.patient[col.id] === "NaN" ? "-" : props.patient[col.id] : "-"}
                                </div>
                            )
                        }
                    })
                }
            </div>
            {
                !isExpanded && noteText && (
                    <div style={{ display: 'block', width: '100%', cursor: 'default' }}>
                        <div style={{display: 'inline-block'}}>
                            <div style={{ paddingLeft: props.padNotes ? '32px' : '0px', display: 'inline-block', color: '#999' }}>
                                {/** @ts-ignore */}
                                {noteText}
                            </div>
                        </div>
                    </div>
                )
            }

            {isExpanded && !props.isPlaceholder &&
                <div style={{ cursor: 'default', display: 'block', width: '100%', paddingTop: '12px', }}>

                    <Row>
                        <Col md={4} style={{paddingLeft: '4px', paddingRight: '0px'}}>
                            {
                                isLoadingLocations ? (
                                    <>
                                        {
                                            [1, 2, 3].map((v) => (
                                                <Placeholder as="p" animation="glow">
                                                    <Placeholder xs={6} size="xs" />
                                                </Placeholder>
                                            ))
                                        }
                                    </>
                                ) : (
                                    <div style={{ paddingLeft: '62px' }}>
                                        <div className="timeline-line"></div>
                                        {events.map(event => (
                                            <Row>
                                                <Col className="timeline-event position-relative">
                                                    <div className="timeline-dot"></div>
                                                    <strong>{event.name}</strong> {getLocalDateStrFromDate(event.time)} {getLocalTimeStrFromDate(event.time)} {timeZoneAbbreviation}
                                                    {event.duration ?? ""}
                                                </Col>
                                            </Row>
                                        ))}
                                    </div>
                                )
                            }
                        </Col>
                        <Col md={4} style={{paddingRight: '0px'}}>
                            <div>
                                <FontAwesomeIcon icon={faBed} fixedWidth />{' '}CSN: <strong><PersonalIdentifier identifier={props.patient.csn} /></strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faUser} fixedWidth />{' '}MRN: <strong><PersonalIdentifier identifier={props.patient.mrn} /></strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faLevelUp} fixedWidth />{' '}Level-of-Care: <strong>{props.patient.admit_level_of_care ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faEnvelope} fixedWidth />{' '}Email: <strong>{props.patient.email ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faPhone} fixedWidth />{' '}Home Phone: <strong>{props.patient.phone_home ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faMobile} fixedWidth />{' '}Mobile: <strong>{props.patient.phone_mobile ?? "-"}</strong>
                            </div>
                        </Col>
                        <Col md={4} style={{paddingRight: '0px'}}>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Ethnicity: <strong>{props.patient.ethnicity ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Race: <strong>{props.patient.race ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faTruckMedical} fixedWidth />{' '}Means of Arrival: <strong>{props.patient.means_of_arrival ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faCreditCard} fixedWidth />{' '}Payor: <strong>{props.patient.payor_class ?? "-"}</strong>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{paddingRight: '4px'}}>
                            <Card className="trackboard-notes-container">
                                <Card.Body>
                                    <ExpandingTextarea setText={handleNoteChange} text={noteText} sendChat={() => { } } placeholder={"Add your notes for this patient visit..."} isDisabled={false} />
                                    <Row style={{position: 'absolute', bottom: '12px', right: '12px'}}>
                                        <Col className="text-right">
                                            {
                                                isSavingNote ? (
                                                    <><FontAwesomeIcon icon={faSpinner} spin />{' '}Saving...</>
                                                ) : noteText === lastSavedNoteText ? (
                                                    <>Saved</>
                                                ) : (
                                                    <>Not Saved</>
                                                )
                                            }
                                        </Col>
                                    </Row>

                                    {
                                        pastNotes && pastNotes.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={12} style={{paddingRight: '4px'}}>
                                                        {
                                                            pastNotes.map((note) => (
                                                                <>
                                                                    <hr />
                                                                    <Row>
                                                                        <Col style={{color: '#999999'}}>
                                                                            <strong>Previous Note</strong> &bull; CSN {note.csn} &bull; Last updated at: {getUserFriendlyDate(note.creation_time)}
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col>
                                                                            {note.note}
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            ))
                                                        }
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    }
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
            }
        </ListGroupItem>
    );
};
